// Content components that are mostly markup: badge, tag, alert, card, links.
import { I80Element, define, h, nextId, lateLabel } from "../base.js";
import { icon } from "../icons.js";

/** A link or button the element renders into (or the author's own <a>/<button>). */
function adoptControl(host, { preferLink = false } = {}) {
  let c = host.part("control") ?? host.child("a, button");
  if (!c) {
    c = preferLink || host.hasAttribute("href") ? h("a") : h("button", { type: host.getAttribute("type") || "button" });
    c.append(...host.takeContent());
    host.append(c);
  }
  c.setAttribute("data-i80-part", "control");
  return c;
}

/** Badge, Tag and the links all render one label; late text replaces it. */
function adoptLabel(host) {
  const label = lateLabel(host);
  if (label) host._label = label;
}

function linkAttrs(host, a) {
  if (a.localName !== "a") return;
  if (host.hasAttribute("href")) a.setAttribute("href", host.getAttribute("href"));
  if (host.flag("external")) {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  }
}

/** Text the author wrote, remembered across updates (the element re-renders its children). */
function labelOf(host, control, textSelector) {
  const existing = control.querySelector(textSelector);
  return host.getAttribute("text") ?? (existing ?? control).textContent.trim();
}

// --- Badge ---------------------------------------------------------------

export class I80Badge extends I80Element {
  static get observedAttributes() {
    return ["color", "size", "type", "icon", "icon-only", "text"];
  }

  setup() {
    this._box = this.part("box") ?? h("div", { "data-i80-part": "box" });
    this._label = labelOf(this, this._box.isConnected ? this._box : this, ".i80-badge__text, .sr-only");
    if (!this._box.isConnected) {
      this.replaceChildren(this._box);
    }
  }

  adopt() {
    adoptLabel(this);
  }

  update() {
    const size = this.choice("size", ["small", "medium", "large"], "medium");
    const name = this.getAttribute("icon");
    const label = this.getAttribute("text") ?? this._label;
    this._box.className = `i80-badge i80-badge--size-${size} i80-badge--type-${this.choice("type", ["filled", "inverted", "outlined"], "filled")} i80-badge--color-${this.choice("color", ["neutral", "neutral-dark-mode", "highlight", "success", "warning", "critical"], "neutral")}`;
    this._box.replaceChildren(
      ...(name ? [h("div", { class: "i80-badge__icon" }, icon(name, { stretched: true }))] : []),
      this.flag("icon-only") && name ? h("span", { class: "sr-only" }, label) : h("div", { class: "i80-badge__text" }, label),
    );
  }
}

// --- Tag -----------------------------------------------------------------

/**
 * <i80-tag>Draft</i80-tag>, <i80-tag href="/tags/draft" color="secondary">Draft</i80-tag>,
 * <i80-tag dismissible>Draft</i80-tag> (fires a cancelable "i80-dismiss" event, then removes itself).
 */
export class I80Tag extends I80Element {
  static get observedAttributes() {
    return ["href", "color", "dismissible", "dismiss-label", "text", "external"];
  }

  setup() {
    this._box = this.part("box") ?? h("span", { "data-i80-part": "box" });
    this._label = this.getAttribute("text") ?? (this._box.isConnected
      ? this._box.querySelector(".i80-tag__text-container")?.textContent ?? ""
      : this.textContent).trim();
    if (!this._box.isConnected) this.replaceChildren(this._box);
  }

  adopt() {
    adoptLabel(this);
  }

  update() {
    const href = this.getAttribute("href");
    const label = this.getAttribute("text") ?? this._label;
    this._box.className = ["i80-tag", href && `i80-tag--color-${this.choice("color", ["primary", "secondary"], "primary")}`,
      href ? "i80-tag--is-interactive" : "i80-tag--is-static"].filter(Boolean).join(" ");
    const text = h("span", { class: "i80-text i80-typography-body-100 i80-font-weight-medium i80-tag__text-container" }, label);
    const parts = [];
    if (this.flag("dismissible")) {
      const btn = h("button", { class: "i80-tag__dismiss", type: "button", "aria-label": `${this.getAttribute("dismiss-label") ?? "Dismiss"} ${label}` },
        icon("x", { className: "i80-tag__dismiss-icon" }));
      btn.addEventListener("click", () => {
        if (this.dispatchEvent(new CustomEvent("i80-dismiss", { bubbles: true, cancelable: true }))) this.remove();
      });
      parts.push(btn);
    }
    if (href) {
      const a = h("a", { class: "i80-tag__link", href }, text);
      linkAttrs(this, a);
      parts.push(a);
    } else {
      parts.push(h("span", { class: "i80-tag__text" }, text));
    }
    this._box.replaceChildren(...parts);
  }
}

// --- Alert ---------------------------------------------------------------

const ALERT_ICONS = { neutral: "info", highlight: "info", success: "check-circle", warning: "alert-triangle", critical: "alert-diamond" };
const ACTION = "i80-button, i80-link-standalone, button, a";

/**
 * <i80-alert type="inline" color="critical" heading="Payment failed" dismissible>
 *   Your card was declined.
 *   <i80-button color="secondary">Update card</i80-button>
 * </i80-alert>
 *
 * Text becomes the description; buttons and links become the actions (made small).
 * dismissible fires a cancelable "i80-dismiss" event, then removes the alert.
 */
export class I80Alert extends I80Element {
  static get observedAttributes() {
    return ["type", "color", "icon", "heading", "dismissible", "dismiss-label"];
  }

  setup() {
    let box = this.part("box");
    if (!box) {
      const nodes = this.takeContent();
      const actions = nodes.filter((n) => n.nodeType === 1 && n.matches(ACTION));
      const described = nodes.filter((n) => !actions.includes(n));
      box = h("div", { "data-i80-part": "box" },
        h("div", { class: "i80-alert__content", "data-i80-part": "content" },
          h("div", { "data-i80-part": "text" },
            h("div", { class: "i80-alert__description i80-font-weight-regular i80-foreground-primary", "data-i80-part": "description" }, described)),
          h("div", { class: "i80-alert__actions", "data-i80-part": "actions" }, actions)));
      this.append(box);
    }
    this._box = box;
    this._description = box.querySelector('[data-i80-part="description"]');
    this._actions = box.querySelector('[data-i80-part="actions"]');
    for (const b of box.querySelectorAll('[data-i80-part="actions"] > i80-button, [data-i80-part="actions"] > i80-link-standalone')) {
      if (!b.hasAttribute("size")) b.setAttribute("size", "small");
    }
  }

  adopt() {
    for (const node of this.takeContent()) {
      const isAction = node.nodeType === 1 && node.matches(ACTION);
      (isAction ? this._actions : this._description).append(node);
      if (isAction && !node.hasAttribute("size") && node.localName.startsWith("i80-")) node.setAttribute("size", "small");
    }
  }

  update() {
    const box = this._box;
    const type = this.choice("type", ["page", "inline", "compact"], "inline");
    const color = this.choice("color", Object.keys(ALERT_ICONS), "neutral");
    box.className = `i80-alert i80-alert--type-${type} i80-alert--color-${color}`;

    const textBox = box.querySelector('[data-i80-part="text"]');
    textBox.className = `i80-alert__text ${type === "compact" ? "i80-typography-body-100" : "i80-typography-body-200"}`;
    textBox.querySelector(":scope > .i80-alert__title")?.remove();
    const heading = this.getAttribute("heading");
    let titleId = null;
    if (heading) {
      titleId = nextId("i80-alert-title");
      textBox.prepend(h("div", { class: "i80-alert__title i80-typography-body-200 i80-font-weight-semibold", id: titleId }, heading));
    }
    const description = textBox.querySelector('[data-i80-part="description"]');
    description.hidden = !description.textContent.trim() && !description.children.length;
    if (!description.id) description.id = nextId("i80-alert-description");

    box.querySelector(":scope > .i80-alert__icon")?.remove();
    const iconAttr = this.getAttribute("icon");
    if (iconAttr !== "false") {
      const name = iconAttr ?? (type === "compact" ? `${ALERT_ICONS[color]}-fill` : ALERT_ICONS[color]);
      box.prepend(h("div", { class: "i80-alert__icon" }, icon(name, { size: type === "compact" ? "16" : "24", stretched: true })));
    }

    const actions = box.querySelector('[data-i80-part="actions"]');
    actions.hidden = !actions.children.length;
    const semantic = ["warning", "critical", "success"].includes(color);
    if (semantic) {
      box.setAttribute("role", actions.children.length ? "alertdialog" : "alert");
      box.setAttribute("aria-live", "polite");
      box.setAttribute("aria-labelledby", titleId ?? description.id);
    } else {
      for (const a of ["role", "aria-live", "aria-labelledby"]) box.removeAttribute(a);
    }

    box.querySelector(":scope > .i80-alert__dismiss")?.remove();
    if (this.flag("dismissible")) {
      const btn = h("button", { class: "i80-dismiss-button i80-alert__dismiss", type: "button", "aria-label": this.getAttribute("dismiss-label") ?? "Dismiss" }, icon("x"));
      btn.addEventListener("click", () => {
        if (this.dispatchEvent(new CustomEvent("i80-dismiss", { bubbles: true, cancelable: true }))) this.remove();
      });
      box.append(btn);
    }
  }
}

// --- Card ----------------------------------------------------------------

/** <i80-card level="mid" level-hover="high" border background="neutral-secondary">...</i80-card> */
export class I80Card extends I80Element {
  static get observedAttributes() {
    return ["level", "level-hover", "level-active", "border", "background", "overflow"];
  }

  setup() {
    this._box = this.part("box");
    if (!this._box) {
      // Always a div: an <li> here would sit inside this element, not directly in the
      // list, which is invalid. In HTML you write the <li> yourself around the card.
      this._box = h("div", { "data-i80-part": "box" }, this.takeContent());
      this.append(this._box);
    }
  }

  adopt() {
    this._box.append(...this.takeContent());
  }

  update() {
    const levels = ["base", "mid", "high"];
    const kind = this.flag("border") ? "surface" : "elevation";
    const hover = this.getAttribute("level-hover");
    const active = this.getAttribute("level-active");
    this._box.className = [
      "i80-card__container",
      `i80-card__container--level-${kind}-${this.choice("level", levels, "base")}`,
      hover && `i80-card__container--hover-level-${kind}-${this.choice("level-hover", levels, "base")}`,
      active && `i80-card__container--active-level-${kind}-${this.choice("level-active", levels, "base")}`,
      `i80-card__container--background-${this.choice("background", ["neutral-primary", "neutral-secondary"], "neutral-primary")}`,
      `i80-card__container--overflow-${this.choice("overflow", ["hidden", "visible"], "visible")}`,
    ].filter(Boolean).join(" ");
  }
}

// --- Links ---------------------------------------------------------------

/** <i80-link-inline href="/docs" icon="arrow-right">Read more</i80-link-inline>, for links inside text. */
export class I80LinkInline extends I80Element {
  static get observedAttributes() {
    return ["href", "color", "icon", "icon-position", "external"];
  }

  setup() {
    this._a = adoptControl(this, { preferLink: true });
    this._label = labelOf(this, this._a, ".i80-link-inline__text");
  }

  adopt() {
    adoptLabel(this);
  }

  update() {
    const pos = this.choice("icon-position", ["leading", "trailing"], "trailing");
    const name = this.getAttribute("icon");
    this._a.className = `i80-link-inline i80-link-inline--color-${this.choice("color", ["primary", "secondary"], "primary")} i80-link-inline--icon-${pos}`;
    linkAttrs(this, this._a);
    const ic = name ? h("span", { class: `i80-link-inline__icon i80-link-inline__icon--${pos}` }, icon(name, { stretched: true })) : null;
    const text = h("span", { class: "i80-link-inline__text" }, this._label);
    text.style.display = "contents";
    this._a.replaceChildren(...(ic ? (pos === "leading" ? [ic, text] : [text, ic]) : [text]));
  }
}

/** <i80-link-standalone href="/plans" icon="arrow-left">All plans</i80-link-standalone>, a link on its own line. */
export class I80LinkStandalone extends I80Element {
  static get observedAttributes() {
    return ["href", "color", "icon", "icon-position", "size", "external", "text"];
  }

  setup() {
    this._a = adoptControl(this, { preferLink: true });
    this._label = labelOf(this, this._a, ".i80-link-standalone__text");
  }

  adopt() {
    adoptLabel(this);
  }

  update() {
    const size = this.choice("size", ["small", "medium", "large"], "medium");
    const pos = this.choice("icon-position", ["leading", "trailing"], "leading");
    const name = this.getAttribute("icon") ?? "arrow-right";
    this._a.className = `i80-link-standalone i80-link-standalone--size-${size} i80-link-standalone--color-${this.choice("color", ["primary", "secondary"], "primary")} i80-link-standalone--icon-position-${pos}`;
    linkAttrs(this, this._a);
    const ic = h("span", { class: "i80-link-standalone__icon" }, icon(name, { size: size === "large" ? "24" : "16", stretched: true }));
    const text = h("span", { class: "i80-link-standalone__text" }, this.getAttribute("text") ?? this._label);
    this._a.replaceChildren(...(pos === "leading" ? [ic, text] : [text, ic]));
  }
}

define("i80-badge", I80Badge);
define("i80-tag", I80Tag);
define("i80-alert", I80Alert);
define("i80-card", I80Card);
define("i80-link-inline", I80LinkInline);
define("i80-link-standalone", I80LinkStandalone);
